import { Box, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import KeniOrb from '@/components/wrapped/KeniOrb';

// The onboarded bottom nav, with Keni docked and raised in the centre so the orb
// is always there to ask something. Tabs sit either side of a centre gap the orb
// rises out of. Wrapped component — it owns its styling.
export type NavTab = 'home' | 'tasks' | 'care' | 'claims';

type Item = { id: NavTab; label: string; icon: string };

const LEFT: Item[] = [
  { id: 'home', label: 'Home', icon: 'house' },
  { id: 'tasks', label: 'Tasks', icon: 'list-check' },
];
const RIGHT: Item[] = [
  { id: 'care', label: 'Care', icon: 'heart' },
  { id: 'claims', label: 'Claims', icon: 'sterling-sign' },
];

export type NorthStarNavProps = {
  active: NavTab;
  onSelect: (tab: NavTab) => void;
  onAskKeni: () => void;
};

const TabCell = ({
  item,
  active,
  onSelect,
}: {
  item: Item;
  active: boolean;
  onSelect: (tab: NavTab) => void;
}) => (
  <Stack
    component='button'
    onClick={() => onSelect(item.id)}
    spacing={0.25}
    sx={{
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      minWidth: 0,
      color: active ? 'primary.main' : 'custom.neutral.400',
      border: 'none',
      bgcolor: 'transparent',
      cursor: 'pointer',
      py: 1,
    }}
  >
    <Icon icon={item.icon as never} type='fal' fontSize={20} />
    <Typography variant='caption' color='inherit'>
      {item.label}
    </Typography>
  </Stack>
);

const NorthStarNav = ({ active, onSelect, onAskKeni }: NorthStarNavProps) => (
  <Box sx={{ flexShrink: 0, bgcolor: 'background.paper' }}>
    <Box
      sx={{
        position: 'relative',
        borderTop: 1,
        borderColor: 'custom.neutral.200',
      }}
    >
      {/* Keni, docked and raised out of the centre */}
      <Box
        component='button'
        onClick={onAskKeni}
        aria-label='Ask Keni'
        sx={{
          position: 'absolute',
          left: '50%',
          top: 0,
          transform: 'translate(-50%, -55%)',
          border: 'none',
          bgcolor: 'transparent',
          cursor: 'pointer',
          p: 0,
        }}
      >
        <KeniOrb size={52} state='idle' />
      </Box>

      <Stack sx={{ alignItems: 'stretch', height: 60 }} direction='row'>
        {LEFT.map((item) => (
          <TabCell
            key={item.id}
            item={item}
            active={active === item.id}
            onSelect={onSelect}
          />
        ))}
        {/* centre gap the orb rises from */}
        <Box sx={{ width: 64, flexShrink: 0 }} />
        {RIGHT.map((item) => (
          <TabCell
            key={item.id}
            item={item}
            active={active === item.id}
            onSelect={onSelect}
          />
        ))}
      </Stack>
    </Box>
    {/* slim home indicator — a trimmed bottom safe area */}
    <Box
      sx={{
        height: 22,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        pb: 0.75,
      }}
    >
      <Box
        sx={{
          width: 134,
          height: 5,
          borderRadius: `${radius.xxl}px`,
          bgcolor: 'text.primary',
          opacity: 0.9,
        }}
      />
    </Box>
  </Box>
);

NorthStarNav.displayName = 'NorthStarNav';
export default NorthStarNav;
